/**
 * EditModeShell — shared layout primitive for image edit modes (crop, text-overlay).
 *
 * Wraps the asset column with:
 *   1. Top mode-bar  → [X cancel] [title] [toolSlot] [Done]
 *   2. Property sub-bar (optional, only when child passes `propertyBar`)
 *   3. Canvas (children) — `bg-canvas`
 *   4. Zoom pill bottom-right (optional)
 *
 * Replaces the four floating containers that previously cluttered text-overlay
 * and the bottom-pill in crop. Per Jobs/Rams:
 *   - One container, one cognitive frame: "I'm editing X. Cancel undoes. Done commits."
 *   - All chrome lives in `bg-canvas` (warm off-white) so the asset is the hero.
 *   - 44px tap targets via padding inside h-11 bar.
 *   - Esc cancels, Cmd+Enter commits.
 *
 * Spec: docs/superpowers/specs/2026-05-04-edit-mode-shell-implementation.md
 */
import { MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { useEffect } from 'react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ITEM_TITLE } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export interface EditModeShellProps {
  /** Mode title shown in the top bar (e.g. "Editing text", "Cropping"). Hidden below sm. */
  title: string;

  /** Mode-specific tool — sits between title and Done. Aspect dropdown (crop), Add text (text). */
  toolSlot?: ReactNode;

  /** Primary commit action. Always "Done" by convention. */
  onDone: () => void;
  doneDisabled?: boolean;
  doneLoading?: boolean;
  doneTooltip?: string;

  /** Cancel handler. Bound to X button + Esc. */
  onCancel: () => void;

  /** Optional property sub-bar — only rendered when provided. */
  propertyBar?: ReactNode;

  /** Canvas content. */
  children: ReactNode;

  /** Optional zoom pill — bottom-right floating control. */
  zoom?: {
    value: number;
    min?: number;
    max?: number;
    onChange: (next: number) => void;
  };

  /** Suppress Esc/Cmd+Enter handling (e.g. when a nested input is focused and consuming keys). */
  disableKeyboard?: boolean;
}

const DEFAULT_ZOOM_MIN = 0.2;
const DEFAULT_ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;

export function EditModeShell({
  title,
  toolSlot,
  onDone,
  doneDisabled,
  doneLoading,
  doneTooltip,
  onCancel,
  propertyBar,
  children,
  zoom,
  disableKeyboard,
}: EditModeShellProps) {
  // Keyboard: Esc → cancel, Cmd+Enter → done.
  // Don't intercept when the user is typing in an input/textarea (text-edit mode
  // double-click opens an inline editor; we mustn't steal Esc from it).
  useEffect(() => {
    if (disableKeyboard) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isTyping =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target && target.isContentEditable);
      if (isTyping) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
        return;
      }
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;
      if (cmdOrCtrl && e.key === 'Enter' && !doneDisabled) {
        e.preventDefault();
        onDone();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onCancel, onDone, doneDisabled, disableKeyboard]);

  const zoomMin = zoom?.min ?? DEFAULT_ZOOM_MIN;
  const zoomMax = zoom?.max ?? DEFAULT_ZOOM_MAX;

  return (
    <div className="relative flex h-full min-h-0 w-full flex-col">
      {/* Top mode-bar — z-30 so it can never be visually overlapped by canvas
          content (Konva Stage absolute children, react-image-crop overlays). */}
      <div className="relative z-30 flex h-11 shrink-0 items-center gap-3 border-b border-border/40 bg-canvas px-3 sm:px-4">
        {/* iOS NavBar pattern: text "Cancel" on the left, "Done" on the right.
            Replaces the previous X icon — the X visually duplicated the
            dialog-close X in the right rail header (same icon, same size,
            both top of dialog), making it ambiguous which one closed what.
            "Cancel" + "Done" pair is unambiguous. */}
        <button
          type="button"
          onClick={onCancel}
          disabled={doneLoading}
          title="Cancel (Esc)"
          className="h-7 shrink-0 rounded-full px-3 text-caption font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
        >
          Cancel
        </button>
        <span className={cn('hidden sm:inline', ITEM_TITLE)}>{title}</span>
        <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
          {toolSlot}
          <Button
            type="button"
            variant="neutral"
            size="sm"
            onClick={onDone}
            disabled={doneDisabled || doneLoading}
            title={doneDisabled ? doneTooltip : undefined}
            className="h-8 px-4 font-semibold"
          >
            {doneLoading ? (
              <>
                <Spinner size="sm" className="mr-1.5 text-primary-foreground/80" />
                Saving…
              </>
            ) : (
              'Done'
            )}
          </Button>
        </div>
      </div>

      {/* Property sub-bar — z-20 above canvas (z-0). Same bg-canvas, hairline border below. */}
      {propertyBar ? (
        <div className="relative z-20 shrink-0 border-b border-border/40 bg-canvas">
          {propertyBar}
        </div>
      ) : null}

      {/* Canvas — z-0 explicit so its absolute-positioned children (Konva Stage,
          react-image-crop overlay) can never visually escape above the bars. */}
      <div className="relative z-0 flex flex-1 min-h-0 items-center justify-center overflow-hidden bg-canvas p-4 md:p-6">
        {children}

        {/* Single zoom pill, bottom-right — the only float allowed on the canvas. */}
        {zoom ? (
          <TooltipProvider delayDuration={300}>
            <div
              className={cn(
                'pointer-events-auto absolute bottom-4 right-4 z-20 flex items-center gap-0.5',
                'rounded-full border border-border/60 bg-background/85 px-1 py-0.5',
                'shadow-elevation-2 backdrop-blur-md',
              )}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => zoom.onChange(Math.max(zoomMin, zoom.value - ZOOM_STEP))}
                    aria-label="Zoom out"
                    className="grid size-6 place-items-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <MagnifyingGlassMinusIcon className="size-3.5" weight="regular" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">Zoom out</TooltipContent>
              </Tooltip>
              <span className="w-9 text-center text-detail font-medium tabular-nums text-muted-foreground">
                {Math.round(zoom.value * 100)}%
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => zoom.onChange(Math.min(zoomMax, zoom.value + ZOOM_STEP))}
                    aria-label="Zoom in"
                    className="grid size-6 place-items-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <MagnifyingGlassPlusIcon className="size-3.5" weight="regular" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">Zoom in</TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        ) : null}
      </div>
    </div>
  );
}
